import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { prepareTransactionsWithEventsExportSimplified } from '../../../../../platform/ux/Campaigns/campaigndetails/utils';
import { defaultAttributionWindow } from '../../../../utils/constants/constants';
import moment from 'moment';

const CampaignChartsController = ({
  children,
  dataConfig,
  url,
  expiresAt,
  refetch,
  byTransactionsRangeDate,
  setByTransactionsRangeDate,
  dataTransactionsRangeDate,
  setDataTransactionsRangeDate,
  getBasketItems,
  basketItemsLoading,
  basketItemsError,
  basketItemsData,
  transactionTarget,
  campaignOrder,
  byCampaignRangeDate,
  setByCampaignRangeDate,
  dataCampaignRangeDate,
  setDataCampaignRangeDate,
  setDataAttributionWindow,
  dataAttributionWindow,
  attrMethod,
  chartData,
  setChartData,
  selectedCampaign,
  dateTrue,
  setDateTrue,
  priceVisible,
  reportName,
  setUseDynamicExport,
  setExportButtonLoading,
  setExportButtonDisable,
  setExportButtonMenuItems,
  getCSVReport,
  useDynamicExport,
  csvReportLoading,
  csvReportDataSignedURL,
  getTransactionsTable,
  transactionsTableLoading,
  transactionsTable,
  pageSize,
  currentUser,
  ...props
}) => {
  let defaultDisplayAttributionWindow =
    (dataConfig && dataConfig.defaultDisplayAttributionWindow) || defaultAttributionWindow.key;

  const [rawData, setRawData] = useState(null);
  const [loadingRawData, setLoadingRawData] = useState(true);
  const [isValidUrl, setIsValidUrl] = useState(null);
  const [s3Url, setS3Url] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [currentStartEndDate, setCurrentStartEndDate] = useState(null);
  const [attributionWindow, setAttributionWindow] = useState(defaultDisplayAttributionWindow);
  const [attributionMethod, setAttributionMethod] = useState('ALL');

  //Time check happens first
  useEffect(() => {
    const currentTime = new Date().toUTCString();
    if (currentTime <= expiresAt) {
      //url is valid - set url
      setS3Url(url);
      setIsValidUrl(true);
    } else {
      //url is valid - refetch
      setIsValidUrl(false);
      refetch();
      setS3Url(null);
    }
  }, [expiresAt, s3Url, setS3Url, refetch, url]);

  //If check happens - we have a url (from reload or first load)
  useEffect(() => {
    if (s3Url && loadingRawData && campaignOrder.version === 1.3) {
      fetch(s3Url).then(async (response) => {
        if (response) {
          try {
            await response.json().then((json) => {
              setRawData(json);
              setLoadingRawData(false);
            });
          } catch (err) {
            setLoadingRawData(false);
          }
        }
      });
    } else if (campaignOrder.version < 1.3 && chartData && chartData.transactionsOverallData) {
      setRawData(chartData);
      setLoadingRawData(false);
    }
  }, [s3Url, loadingRawData, campaignOrder, chartData]);

  useEffect(() => {
    if (rawData && rawData.transactionsWithEvents && !chartData.transactionsWithEvents) {
      setChartData({
        ...chartData,
        transactionsWithEvents: rawData.transactionsWithEvents,
      });
    }
  }, [rawData, chartData, setChartData]);

  if (rawData && !rawData.hasSimplifiedReport && useDynamicExport) {
    setUseDynamicExport(false);
  }

  useEffect(() => {
    if (useDynamicExport === true) {
      setExportButtonDisable(false);
      setExportButtonMenuItems([
        {
          key: 'transactionEventsSource',
          text: 'Transaction - With Events',
          tooltip:
            'This report lists all transactions with Impression events but does not allow filtering by date.',
          confirmTitle: 'Please allow Popup',
          confirmDescription: 'Popup should be allowed on your browser to download this report',
          hasConfirm: true,
          onClick: () => {
            setExportButtonLoading(true);
            getCSVReport();
          },
        },
        {
          key: 'transactionEvents',
          text: 'Transaction - Without Events',
          tooltip:
            'This report lists all transactions excluding Impression events and allows filtering by date.',
          onClick: () => {
            setExportButtonLoading(true);
            const key = 'TransactionsWithEvents';
            const zip = new JSZip();
            const folder = zip.folder(`${selectedCampaign.name.replace(/\//g, ' ')}-${key}}`);
            const formattedData =
              prepareTransactionsWithEventsExportSimplified(dataAttributionWindow);
            folder.file(
              `${selectedCampaign.name} - Transaction - With Events.csv`,
              Papa.unparse(formattedData)
            );
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(
                  blob,
                  `${selectedCampaign.name.replace(/\//g, ' ')}-Transaction - With Events`
                );
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
        {
          key: 'transactionsWithEventsDailyTotal',
          text: 'Transaction - With Events (Daily Total)',
          confirmTitle: 'Please allow Popup',
          confirmDescription: 'Popup should be allowed on your browser to download this report',
          hasConfirm: true,
          onClick: () => {
            setExportButtonLoading(true);
            let groupedTransactions = dataAttributionWindow
              .reduce((accumulator, transaction) => {
                // Extract date from transaction timestamp
                const transactionDate = moment(transaction.transaction, 'YYYY-MM-DD').format(
                  'YYYY-MM-DD'
                );
                const idx = accumulator.findIndex(
                  (item) => item['Transaction Date'] === transactionDate
                );
                if (idx !== -1) {
                  accumulator[idx]['Number of Transactions'] += 1;
                  accumulator[idx]['Transaction Total'] += transaction.transactionTotal;
                  accumulator[idx]['Transaction Tax'] += transaction.transactionTax;
                  accumulator[idx]['Transaction Shipping'] += transaction.transactionShipping;
                } else {
                  accumulator.push({
                    'Transaction Date': transactionDate,
                    'Number of Transactions': 1,
                    'Transaction Total': transaction.transactionTotal,
                    'Transaction Tax': transaction.transactionTax,
                    'Transaction Shipping': transaction.transactionShipping,
                  });
                }

                return accumulator;
              }, [])
              .sort((a, b) => moment(a['Transaction Date']).diff(moment(b['Transaction Date'])));
            const key = 'transactionsWithEventsDailyTotal';
            const zip = new JSZip();
            const folder = zip.folder(`${selectedCampaign.name.replace(/\//g, ' ')}-${key}}`);

            folder.file(
              `${selectedCampaign.name} - Transaction - With Events (Daily Total).csv`,
              Papa.unparse(groupedTransactions)
            );
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(
                  blob,
                  `${selectedCampaign.name.replace(
                    /\//g,
                    ' '
                  )}-Transaction - With Events (Daily Total)`
                );
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
        {
          key: 'transactionEventsCreatives',
          text: 'Transaction - With Events - (Creatives)',
          confirmTitle: 'Please allow Popup',
          confirmDescription: 'Popup should be allowed on your browser to download this report',
          hasConfirm: true,
          onClick: () => {
            setExportButtonLoading(true);
            let creatives = rawData.transactionsCreatives;
            const key = 'transactionsWithEventsDailyTotal';
            const zip = new JSZip();
            const folder = zip.folder(`${selectedCampaign.name.replace(/\//g, ' ')}-${key}}`);

            folder.file(
              `${selectedCampaign.name} - Transaction - With Events - (Creatives).csv`,
              Papa.unparse(
                creatives.map((c) => {
                  return {
                    'Creative ID': c.CREATIVEID,
                    Impressions: c.impressions,
                    'Total Transactions': c.totalTransactions,
                    'Creative URL': c.CREATIVEURL,
                    'Target URL': c.TARGETURL,
                  };
                })
              )
            );
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(
                  blob,
                  `${selectedCampaign.name.replace(
                    /\//g,
                    ' '
                  )}-Transaction - With Events - (Creatives)`
                );
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
        {
          key: 'transactionEventsPublishers',
          text: 'Transaction - With Events - (Publishers)',
          confirmTitle: 'Please allow Popup',
          confirmDescription: 'Popup should be allowed on your browser to download this report',
          hasConfirm: true,
          onClick: () => {
            setExportButtonLoading(true);
            let publisher = rawData.transactionsPublishers;
            const key = 'transactionEventsPublishers';
            const zip = new JSZip();
            const folder = zip.folder(`${selectedCampaign.name.replace(/\//g, ' ')}-${key}}`);

            folder.file(
              `${selectedCampaign.name} - Transaction - With Events - (Publishers).csv`,
              Papa.unparse(
                publisher.map((c) => {
                  return {
                    'Site Name': c.title,
                    Impressions: c.impressions,
                    'Total Transactions': c.totalTransactions,
                  };
                })
              )
            );
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(
                  blob,
                  `${selectedCampaign.name.replace(
                    /\//g,
                    ' '
                  )}-Transaction - With Events - (Publishers)`
                );
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
      ]);
    }
  }, [
    dataAttributionWindow,
    getCSVReport,
    rawData,
    selectedCampaign.name,
    setExportButtonDisable,
    setExportButtonLoading,
    setExportButtonMenuItems,
    useDynamicExport,
  ]);

  useEffect(() => {
    if (csvReportDataSignedURL && csvReportDataSignedURL.getCampaignReportSignedURL) {
      //Download CSV via URL
      const { url } = csvReportDataSignedURL.getCampaignReportSignedURL;
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          saveAs(blob, `${campaignOrder.name.replace(/\//g, ' ')}-transactions.csv`);
          setExportButtonLoading(false);
        })
        .catch((error) => {
          console.error('Error downloading CSV:', error);
          setExportButtonLoading(false);
        });
    }
  }, [csvReportDataSignedURL, campaignOrder.name, setExportButtonLoading]);

  useEffect(() => {
    if (rawData && rawData.hasSimplifiedReport)
      getTransactionsTable({
        variables: {
          campaignOrderId: campaignOrder.id,
          reportName,
          index: currentIdx + 1,
          limit: pageSize,
          startDateTimestamp: currentStartEndDate
            ? moment(currentStartEndDate[0], 'YYYY/MM/DD').unix()
            : null,
          endDateTimestamp: currentStartEndDate
            ? moment(currentStartEndDate[1], 'YYYY/MM/DD').unix()
            : null,
          attributionWindow,
          attrMethod: attributionMethod,
        },
      });
  }, [
    campaignOrder,
    currentIdx,
    getTransactionsTable,
    attributionMethod,
    pageSize,
    reportName,
    currentStartEndDate,
    attributionWindow,
    rawData,
  ]);

  if (isValidUrl === true || (campaignOrder.version < 1.3 && chartData)) {
    return React.cloneElement(children, {
      chartData: rawData,
      chartsLoading: loadingRawData,
      isValidUrl,
      byTransactionsRangeDate,
      setByTransactionsRangeDate,
      dataTransactionsRangeDate,
      setDataTransactionsRangeDate,
      getBasketItems,
      basketItemsLoading,
      basketItemsError,
      basketItemsData,
      transactionTarget,
      campaignOrder,
      byCampaignRangeDate,
      setByCampaignRangeDate,
      dataCampaignRangeDate,
      setDataCampaignRangeDate,
      selectedCampaign,
      dateTrue,
      setDateTrue,
      priceVisible,
      setDataAttributionWindow,
      dataAttributionWindow,
      reportName,
      transactionsTable,
      transactionsTableLoading,
      attributionWindow,
      setCurrentIdx,
      setCurrentStartEndDate,
      setAttributionWindow,
      currentUser,
      attributionMethod,
      setAttributionMethod,
      ...props,
    });
  } else {
    return React.cloneElement(children, {
      chartData: null,
      isValidUrl,
      campaignOrder,
    });
  }
};

export default CampaignChartsController;
